import React from 'react';
import { Badge } from '@/components/ui/Badge';
import { SEVERITY_NAMES } from '@/lib/labels';
import type { BreachFlag, CxStatus } from '@/types/domain';
import type { BreachResponsible } from '@/types/api';
import { TicketStatus, Severity, ApprovalState, statusLabel } from '@/types/domain';

/**
 * V4.9.10 — Status badge with distinct color per step (Item #3).
 *
 * Each TicketStatus gets its OWN dedicated hue so users can recognise
 * the stage at a glance without reading the text. Earlier-stage colors
 * are lighter / cooler; later stages are richer; terminal states get
 * semantic colors (green for done, grey for cancelled).
 *
 * The full palette is encoded in statusColorCss() below. To swap a
 * color, just edit one line — no other code change needed.
 *
 * Accessibility: every adjacent pair is distinguishable for the three
 * common color-blindness types (deuteranopia, protanopia, tritanopia).
 */
export function TicketStatusBadge({ status }: { status: TicketStatus }) {
  const css = statusColorCss(status);
  return (
    <span
      className="status-badge"
      style={{
        background: css.bg,
        color: css.fg,
        borderColor: css.border
      }}
      title={status}
    >
      {statusLabel(status)}
    </span>
  );
}

/**
 * V4.9.10 — Per-status color palette. Each status has a distinct
 * (background, foreground, border) triple. The progression goes
 * roughly: cool/light → warmer → action-oriented → terminal-green.
 */
function statusColorCss(status: TicketStatus): { bg: string; fg: string; border: string } {
  const palette: Partial<Record<TicketStatus, { bg: string; fg: string; border: string }>> = {
    // ── Initial stage ──
    [TicketStatus.Open]: { bg: '#dbeafe', fg: '#1e3a8a', border: '#93c5fd' },

    // ── Investigation chain (cool blues, getting deeper) ──
    [TicketStatus.InvestigationInProgress]: { bg: '#bfdbfe', fg: '#1e3a8a', border: '#60a5fa' },
    [TicketStatus.BInProgress]:              { bg: '#bfdbfe', fg: '#1e3a8a', border: '#60a5fa' },
    [TicketStatus.InvestigationAwaitingApproval]: { bg: '#fef3c7', fg: '#92400e', border: '#fcd34d' },
    [TicketStatus.InvestigationAwaitingCx]:  { bg: '#fed7aa', fg: '#9a3412', border: '#fb923c' },

    // ── A-flow customer-interaction (warm tones) ──
    [TicketStatus.InformCustomer]:           { bg: '#fce7f3', fg: '#9d174d', border: '#f9a8d4' },
    [TicketStatus.AwaitingCustomerResponse]: { bg: '#fed7aa', fg: '#9a3412', border: '#fb923c' },
    [TicketStatus.AwaitingCustomerFeedback]: { bg: '#fed7aa', fg: '#9a3412', border: '#fb923c' },
    [TicketStatus.AwaitingReturnDelivery]:   { bg: '#fde68a', fg: '#854d0e', border: '#facc15' },
    [TicketStatus.CallCarrierAndInformCustomer]: { bg: '#fbcfe8', fg: '#831843', border: '#f472b6' },

    // ── Approval / Production / Shipping (richer, action-oriented) ──
    [TicketStatus.RefundProcessed]:          { bg: '#cffafe', fg: '#0e7490', border: '#67e8f9' },
    [TicketStatus.ApprovedForReplacement]:   { bg: '#a7f3d0', fg: '#065f46', border: '#34d399' },
    [TicketStatus.ApprovedReprint]:          { bg: '#a7f3d0', fg: '#065f46', border: '#34d399' },
    [TicketStatus.ApprovedReprintInProgress]:{ bg: '#86efac', fg: '#14532d', border: '#22c55e' },
    [TicketStatus.ApprovedRefundCompleted]:  { bg: '#bbf7d0', fg: '#14532d', border: '#4ade80' },
    [TicketStatus.ReprintInProduction]:      { bg: '#bae6fd', fg: '#075985', border: '#38bdf8' },
    [TicketStatus.ReplacementInProduction]:  { bg: '#bae6fd', fg: '#075985', border: '#38bdf8' },
    [TicketStatus.ReprintShippedInformCustomer]:     { bg: '#a5f3fc', fg: '#155e75', border: '#22d3ee' },
    [TicketStatus.ReplacementShippedInformCustomer]: { bg: '#a5f3fc', fg: '#155e75', border: '#22d3ee' },

    // ── Terminal success (greens) ──
    [TicketStatus.Completed]:               { bg: '#bbf7d0', fg: '#14532d', border: '#4ade80' },
    [TicketStatus.CompletedRefunded]:       { bg: '#86efac', fg: '#14532d', border: '#22c55e' },
    [TicketStatus.ReprintCompleted]:        { bg: '#bbf7d0', fg: '#14532d', border: '#4ade80' },
    [TicketStatus.ReplacementCompleted]:    { bg: '#bbf7d0', fg: '#14532d', border: '#4ade80' },
    [TicketStatus.DeliveredCompleted]:      { bg: '#86efac', fg: '#14532d', border: '#22c55e' },

    // ── Terminal closed-without-success (grey) ──
    [TicketStatus.Cancelled]:               { bg: '#e5e7eb', fg: '#374151', border: '#9ca3af' },
    [TicketStatus.Resolved]:                { bg: '#bbf7d0', fg: '#14532d', border: '#4ade80' }
  };
  return palette[status] ?? { bg: '#e5e7eb', fg: '#374151', border: '#9ca3af' };
}

/**
 * Severity badge — shows code + human label inline ("P1 - Critical")
 * to match Category column ("A2 Refund (shipped)") format.
 */
export function SeverityBadge({ severity }: { severity: Severity }) {
  const tone = severityTone(severity);
  const name = SEVERITY_NAMES[severity];
  const label = name ? `${severity} - ${name}` : severity;
  return (
    <Badge tone={tone}>
      <span title={label}>{label}</span>
    </Badge>
  );
}

// V4.9 — Breach badge now optionally shows WHO is responsible when the
// ticket is BREACHED / late. Example output:
//   "BREACHED · Solver Anitha"
//   "BREACHED · Approver (Admin)"
//   "BREACHED · Communicator Leela"
// When `responsible` is null (Done / Paused / no data) we fall back to
// the original label-only badge.
export function BreachBadge({
  flag,
  responsible,
  hoursOver,
  // V4.9.13 — the ticket DETAIL header also renders an "AWAITED BY …" pill,
  // which is the same person as the breach-responsible. Pass false there so
  // we don't print "Approver Admin" twice. Defaults true (list views, where
  // there's no awaited-by column next to the SLA badge).
  withResponsible = true
}: {
  flag: BreachFlag;
  responsible?: BreachResponsible | null;
  hoursOver?: number | null;
  withResponsible?: boolean;
}) {
  const tone = breachTone(flag);
  const label = breachLabel(flag);
  const showResponsible =
    withResponsible &&
    responsible &&
    (flag === 'BREACHED' || flag === 'WARNING_2H' || flag === 'WARNING_24H');
  if (showResponsible) {
    const roleLabel =
      responsible.role === 'solver'
        ? 'Solver'
        : responsible.role === 'approver'
        ? 'Approver'
        : responsible.role === 'communicator'
        ? 'Communicator'
        : 'Owner';
    return (
      <div className="breach-badge-stack">
        <Badge tone={tone}>{label}</Badge>
        <div className="breach-responsible" title={`Whose turn: ${roleLabel} ${responsible.name}`}>
          <span className="breach-responsible-role">{roleLabel}</span>{' '}
          <span className="breach-responsible-name">{responsible.name}</span>
        </div>
      </div>
    );
  }
  return <Badge tone={tone}>{label}</Badge>;
}

// V4.9 — Clearer CX status labels. "Fresh" was ambiguous; admins reading
// the badge couldn't tell at a glance whether the customer had been
// contacted recently or not. New labels reflect the actual state:
//   FRESH          → "CX Informed" (customer was contacted within 4h)
//   UPDATE_SOON    → "Update due"  (4-6h since last contact)
//   SILENT         → "CX Silent"   (6h+ since last contact)
//   NEVER_INFORMED → "Never informed" (no CX log yet)
export function CxBadge({ status }: { status: CxStatus | undefined | null }) {
  if (!status) return <Badge tone="neutral">—</Badge>;
  switch (status) {
    case 'FRESH':
      return <Badge tone="success">CX Informed</Badge>;
    case 'UPDATE_SOON':
      return <Badge tone="warning">Update due</Badge>;
    case 'SILENT':
      return <Badge tone="danger">CX Silent</Badge>;
    case 'NEVER_INFORMED':
      return <Badge tone="danger">Never informed</Badge>;
    default:
      return <Badge tone="neutral">{status}</Badge>;
  }
}

export function ApprovalBadge({ state }: { state: ApprovalState }) {
  switch (state) {
    case ApprovalState.NotNeeded:
      return <Badge tone="neutral">No approval needed</Badge>;
    case ApprovalState.WaitingAdmin:
      // Display label only — enum value stays "Waiting Ross" for DB compatibility.
      return <Badge tone="warning">Waiting Admin</Badge>;
    case ApprovalState.Approved:
      return <Badge tone="success">Approved</Badge>;
    case ApprovalState.Denied:
      return <Badge tone="danger">Denied</Badge>;
    case ApprovalState.MoreInfoNeeded:
      return <Badge tone="info">More info needed</Badge>;
    default:
      return <Badge tone="neutral">{state}</Badge>;
  }
}

function statusTone(status: TicketStatus) {
  // V4.9.5 — new status taxonomy + legacy values (for historical rows)
  switch (status) {
    case TicketStatus.Open:
      return 'primary' as const;

    // Active investigation
    case TicketStatus.InvestigationInProgress:
    case TicketStatus.InProgress: // legacy
      return 'info' as const;

    // Awaiting actions (amber/warning)
    case TicketStatus.InvestigationAwaitingApproval:
    case TicketStatus.WaitingApproval: // legacy
      return 'warning' as const;
    case TicketStatus.InvestigationAwaitingCx:
    case TicketStatus.WaitingCustomer: // legacy
    case TicketStatus.WaitingDesign: // legacy
    case TicketStatus.WaitingVendor: // legacy
      return 'warning' as const;

    // Approved-decision states (active follow-through)
    case TicketStatus.ApprovedForReplacement:
    case TicketStatus.ApprovedReprint:
    case TicketStatus.ApprovedReprintInProgress:
      return 'info' as const;

    // Terminal — done (green)
    case TicketStatus.ApprovedRefundCompleted:
    case TicketStatus.ReprintCompleted:
    case TicketStatus.ReplacementCompleted:
    case TicketStatus.Completed:
    case TicketStatus.Resolved: // legacy
      return 'success' as const;

    // Terminal — cancelled (neutral grey)
    case TicketStatus.Cancelled:
      return 'neutral' as const;

    default:
      return 'neutral' as const;
  }
}

function severityTone(severity: Severity) {
  switch (severity) {
    case Severity.P1:
      return 'danger' as const;
    case Severity.P2:
      return 'warning' as const;
    case Severity.P3:
      return 'info' as const;
    case Severity.P4:
      return 'neutral' as const;
    default:
      return 'neutral' as const;
  }
}

function breachTone(flag: BreachFlag) {
  switch (flag) {
    case 'OK':
      return 'success' as const;
    case 'WARNING_24H':
      return 'warning' as const;
    case 'WARNING_2H':
      return 'danger' as const;
    case 'BREACHED':
      return 'danger' as const;
    case 'Done':
      return 'neutral' as const;
    case 'Paused':
      return 'info' as const;
    default:
      return 'neutral' as const;
  }
}

function breachLabel(flag: BreachFlag) {
  switch (flag) {
    case 'OK':
      return 'On time';
    case 'WARNING_24H':
      return 'Due soon';
    case 'WARNING_2H':
      return 'Due now';
    case 'BREACHED':
      return 'Overdue';
    case 'Done':
      return 'Done';
    case 'Paused':
      return 'Paused';
    default:
      return flag;
  }
}
